import React from 'react';
import { useListDestinationsAndDefaults } from 'Pages/Configure/Destinations/ListDestinations';
import { extractErrorMessage } from 'Helpers/utils';
import { useSnackbar } from 'pounceV2';

const useListAvailableDestinations = ({ outputIds }) => {
  const { pushSnackbar } = useSnackbar();

  const { error, data, loading } = useListDestinationsAndDefaults({
    onError: err =>
      pushSnackbar({
        variant: 'error',
        title: 'Could not fetch your destinations',
        description: extractErrorMessage(err),
      }),
  });

  // Create a Map from id => display name
  // Create an array of valid outputIds
  // The user could have deleted a destination that was assigned to a policy/rule
  // which needs to be filtered out and not shown to the user. The data is not
  // overwritten (cleaned) until the user updates the respective field; however, there is
  // no side-effects of having an invalid ID stored in the backend as it is simply
  // ignored.
  const [destinationOutputIds, validOutputIds, mapIdsToDisplayNames] = React.useMemo(() => {
    const mappyBoi = (data?.destinations || []).reduce(
      (result, { outputId, displayName }) => ({ ...result, [outputId]: displayName }),
      {},
    );
    const destIds = Object.keys(mappyBoi);
    const validIds = destIds.filter(outputId => (outputIds || []).includes(outputId));
    return [destIds, validIds, mappyBoi];
  }, [data?.destinations, outputIds]);

  // Lookup an ID and get the corresponding display name
  const destinationIdToDisplayName = React.useCallback(
    (outputId: string) =>
      mapIdsToDisplayNames[outputId] ? mapIdsToDisplayNames[outputId] : outputId,
    [mapIdsToDisplayNames],
  );

  return React.useMemo(
    () => ({
      error,
      destinationOutputIds,
      validOutputIds,
      loading,
      destinationIdToDisplayName,
      disabled: Boolean(error) || (!loading && !destinationOutputIds.length),
    }),
    [error, destinationOutputIds, validOutputIds, loading, destinationIdToDisplayName],
  );
};

export default useListAvailableDestinations;
